import { useEffect, useRef, useState } from "react";
import { ReviewModeSetting } from "../components/ReviewModeSetting";
import { COMMUNITY_INCLUDED } from "../lib/communityFeature";
import { routeQuery } from "../lib/route";
import { SettingsHead } from "../components/AccountTabs";
import { Workspace } from "../components/Shell";
import {
  EMPTY_PROFILE, MAX_ACHIEVEMENTS, loadCoachProfile, publishFault, saveCoachProfile,
  type CoachProfile,
} from "../lib/coachProfile";

/* THE COACH'S OWN PAGE — the profile clients find them by.
 *
 * 🔴 WITHOUT THIS SCREEN A COACH WHO SIGNED UP ON THE WEB IS A DEAD END: invisible in the
 * directory, unable to post, with nothing saying why. The parity walk of 2026-09-13 found
 * `coach_profiles` referenced nowhere under `web/src`, hours after web sign-up shipped.
 *
 * ⚠️ **SAVE AND PUBLISH ARE TWO BUTTONS BECAUSE THEY ARE TWO ACTS**, which is the phone's split
 * too: a half-written page may be saved, and only publishing is gated by `publishFault`. Folding
 * them would mean a coach cannot put a page down and come back to it.
 *
 * ⚠️ **THE STATE LINE IS THE FIRST THING ON THE PAGE**, because "am I findable?" is the question
 * this screen exists to answer. The phone's version of it was buried two levels into a Profile tab
 * and that is precisely why a coach could not tell a deliberate gate from a broken app.
 *
 * ⚠️ **NO PORTRAIT FIELD YET, AND IT SAYS SO.** Avatar upload is a browser-shaped job (a file
 * input, a direct storage upload, a canvas crop) rather than a port of `AvatarPhotoPicker`, and it
 * is queued as its own item. A photo is OPTIONAL for publishing (his 2026-09-12 word), so its
 * absence blocks nothing — but a screen that silently had no way to add one would read as broken.
 */
export function CoachPage({ coachId }: { coachId: string }) {
  const [profile, setProfile] = useState<CoachProfile>(EMPTY_PROFILE);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  /* W21 (E) — whether the phone has opened the form behind the account card.
     ⚠️ IT STARTS CLOSED, because the app's Profile is a short list of rows and the form is a
     PUSHED screen. The desktop ignores it entirely: the band's rule is what hides a closed form,
     and outside the band the form is simply the page. */
  const [formOpen, setFormOpen] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);

  /* Every route must move focus — the rule `b4-7` ruler 10 derives from every file rendering a
   * `<main id="main">`. A client-side navigation moves nothing on its own. */
  useEffect(() => { headingRef.current?.focus(); }, []);

  useEffect(() => {
    let cancelled = false;
    void loadCoachProfile(coachId).then((result) => {
      if (cancelled) return;
      if ("error" in result) setError(result.error);
      else setProfile(result.profile);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [coachId]);

  function set<K extends keyof CoachProfile>(key: K, value: CoachProfile[K]) {
    setProfile((p) => ({ ...p, [key]: value }));
    setSaved(null);
    setError(null);
  }

  async function commit(publish: boolean) {
    setBusy(true);
    const fault = await saveCoachProfile(coachId, profile, publish);
    setBusy(false);
    if (fault !== null) { setError(fault); return; }
    setError(null);
    if (publish) setProfile((p) => ({ ...p, isPublished: true }));
    setSaved(publish ? "Your page is live." : "Saved.");
  }

  const fault = publishFault(profile);

  if (loading) {
    return (
      <Workspace>
      <main id="main" className="account-page coachpage ours-page">
        <SettingsHead current="page" headingRef={headingRef} phoneTitle="Profile" />
        <p className="ht-callout">Loading…</p>
      </main>
      </Workspace>
    );
  }

  /* JOB 3 STEP 5 PAGE 6 — inside the Workspace and on `ours-page`, as the account's other pages are: it was the one tab
     outside both, so its head sat 16 px higher and its row 101 px narrower — the row moved under the pointer between tabs. */
  return (
    <Workspace>
    <main id="main" className="account-page coachpage ours-page">
      {/* 🔴 ONE `<h1>`, TWO MUTUALLY EXCLUSIVE SPANS — the app's fourth tab is called `Profile`
          and holds the account, Appearance, Units and Log Out; ours is `Your coaching page` and
          is a form with nine fields and no way out of it. `headingRef` is the focus target every
          navigation into this screen moves to, so a second `<h1>` would be a second target and a
          ref points at one node. */}
      <SettingsHead current="page" headingRef={headingRef} phoneTitle="Profile" />
      <div className="coachpage-body">

      {/* ══ W21 (E) — THE APP'S ACCOUNT CARD, PHONE ONLY ══
          `WR · William Rehearsal · "Account & location" · ›`, and pressing it is what opens the
          form below. On the desktop the form is simply the page, so this card and its button do
          not render at all.
          ⚠️ **IT IS A REAL `<button aria-expanded>` AND THE FORM IS SHOWN BY A CLASS, NOT BY
          `hidden`.** The desktop must show the form whatever this state says, and a `hidden`
          attribute beats every stylesheet — so the state paints a class the phone band reads and
          the desktop ignores. One state, two widths, no second source of truth.
          ⚠️ AND THE SUBTITLE IS THE APP'S WORDS. `Account & location` is what the app's row says
          under the name; this form is where a coach's city, bio and links are typed, so it is
          the same row about the same thing rather than a new label invented for the web. */}
      {/* 🔴 W120 (Fable's F4): with the Community out, the directory's card and form — the sentence about being found in
          Coaches, and every field only the directory read — are not drawn; what stays is how next week is set (Log Out is the head's since 10-16). */}
      {COMMUNITY_INCLUDED && (<>
      <button type="button" className="coachpage-account"
              aria-expanded={formOpen}
              aria-controls="coachpage-form"
              onClick={() => { setFormOpen((v) => !v); }}>
        <span className="coachpage-account-disc" aria-hidden="true">
          {(profile.displayName.trim() === "" ? "" : profile.displayName)
            .split(/\s+/).map((w) => w[0] ?? "").join("").slice(0, 2).toUpperCase()}
        </span>
        <span className="coachpage-account-text">
          <span className="coachpage-account-name">
            {profile.displayName.trim() === "" ? "Your coaching page" : profile.displayName}
          </span>
          <span className="coachpage-account-sub">Account &amp; location</span>
        </span>
        <span aria-hidden="true" className="coachpage-account-chev">&rsaquo;</span>
      </button>

      <div id="coachpage-form" className={formOpen ? "coachpage-form is-open" : "coachpage-form"}>

      {/* THE ANSWER TO "AM I FINDABLE?", before anything you could edit. */}
      <p className="coachpage-state ht-callout">
        {profile.isPublished
          ? "Live — clients can find you in Coaches, and you can post."
          : "Not published yet — clients cannot find you, and you cannot post."}
      </p>

      {error !== null && <div className="signin-form-error" role="alert">{error}</div>}
      {saved !== null && <p className="signin-notice ht-callout" aria-live="polite">{saved}</p>}

      <div className="ht-field">
        <label className="ht-field-label" htmlFor="displayName">Name clients see</label>
        <input id="displayName" className="ht-input" value={profile.displayName}
               onChange={(e) => set("displayName", e.target.value)} />
      </div>

      <div className="ht-field">
        <label className="ht-field-label" htmlFor="headline">One line about what you coach</label>
        <input id="headline" className="ht-input" value={profile.headline}
               onChange={(e) => set("headline", e.target.value)} />
        {/* The directory shows THIS and little else, which is worth saying where it is typed. */}
        <p className="ht-field-hint">The only thing the Coaches list shows about you.</p>
      </div>

      <div className="ht-field">
        <label className="ht-field-label" htmlFor="bio">About you</label>
        <textarea id="bio" className="ht-input coachpage-bio" rows={6} value={profile.bio}
                  onChange={(e) => set("bio", e.target.value)} />
      </div>

      <div className="coachpage-pair">
        <div className="ht-field">
          <label className="ht-field-label" htmlFor="city">City</label>
          <input id="city" className="ht-input" value={profile.city}
                 onChange={(e) => set("city", e.target.value)} />
        </div>
        <div className="ht-field">
          <label className="ht-field-label" htmlFor="years">Years coaching</label>
          <input id="years" className="ht-input" type="number" min={0} value={profile.yearsCoaching}
                 onChange={(e) => set("yearsCoaching", Number(e.target.value) || 0)} />
        </div>
      </div>

      <div className="coachpage-pair">
        <div className="ht-field">
          <label className="ht-field-label" htmlFor="website">Website</label>
          <input id="website" className="ht-input" value={profile.website}
                 onChange={(e) => set("website", e.target.value)} />
        </div>
        <div className="ht-field">
          <label className="ht-field-label" htmlFor="instagram">Instagram</label>
          <input id="instagram" className="ht-input" value={profile.instagram}
                 onChange={(e) => set("instagram", e.target.value)} />
        </div>
      </div>

      <div className="ht-field">
        <label className="ht-field-label" htmlFor="achievements">
          Achievements — one per line
        </label>
        <textarea id="achievements" className="ht-input coachpage-bio" rows={4}
                  value={profile.achievements.join("\n")}
                  onChange={(e) => set("achievements", e.target.value.split("\n"))} />
        <p className="ht-field-hint">Up to {MAX_ACHIEVEMENTS}. Blank lines are ignored.</p>
      </div>

      {/* ⚠️ THE REASON PUBLISHING IS REFUSED IS SHOWN BESIDE THE BUTTON, NOT INSTEAD OF IT. A
          disabled control with no sentence is the shape people report as "the app is broken" —
          the same rule `CoachProfileDraft.PublishFault` exists for on the phone. */}
      {fault !== null && !profile.isPublished && (
        <p className="coachpage-fault ht-callout">{fault}</p>
      )}

      <div className="coachpage-actions">
        <button className="ht-button" onClick={() => void commit(false)} disabled={busy}>
          {busy ? "Saving…" : "Save"}
        </button>
        <button className="ht-button ht-button-primary" onClick={() => void commit(true)} disabled={busy}>
          {profile.isPublished ? "Save and keep live" : "Publish"}
        </button>
      </div>

      <p className="ht-field-hint coachpage-note">
        A photo is optional, and there is no way to add one here yet — it is coming.
      </p>
      </div>
      </>)}

      {/* ══ W21 (E) — APPEARANCE AND LOG OUT, WHICH THE APP'S PROFILE CARRIES AND THE PHONE HAD
          NOWHERE TO PUT ══
          Screen 1 decided that Sign out is not a tab ("a destructive-ish control sitting in a
          four-icon bar is one mis-tap from ending a session") and said it lives on the account
          screen. This IS the account screen, and until now it did not carry it — so a phone
          coach could not sign out at all.
          ⚠️ W81: APPEARANCE MOVED OUT OF THIS FILE — `ThemeControl` is rendered exactly once, by `Shell`, which draws it
          under this page at phone width. Two controls over one stored value is how they come to disagree.
          ⚠️ **UNITS IS NOT BUILT, AND THAT IS FILED RATHER THAN INVENTED.** The app's Profile has
          a Metric/Imperial switch; the web has NO coach-level units store to write it to
          (`isImperial` reads the CLIENT's `unit_system`, which is the athlete's own answer). A
          switch with no reader is the dead-control defect this product has fixed twice. */}
      {/* W81 (the pro web step 1): the Appearance switch is rendered ONCE, by `Shell` — at phone width it is drawn on THIS
          route, under this section (`shell-phone-appearance`). What stays here is the one thing only this screen offers. */}
      {/* W82 step 4 — how next week is set (`profiles.review_mode`); the review page's `How you set next week ›` lands here. */}
      <ReviewModeSetting coachId={coachId} focus={routeQuery(window.location.hash).show === "review-mode"} />
      {/* Log Out is the Settings HEAD's since the hover row's brief (10-16): `SettingsHead`, the same on every tab. */}
      </div>
    </main>
    </Workspace>
  );
}
